import { useMemo } from 'react';
import { formatEta } from '@galaxis/shared';
import type { FleetClient } from '@galaxis/shared';
import { summarizeFleets } from './fleet-overview.ts';
import { formatDuration } from './fleet-format.ts';
import { useNow } from './FleetIcons.tsx';

interface Props {
  fleets: FleetClient[];
  systemName: (id: string | null) => string;
  onSelect: (id: string) => void;
}

/**
 * Status overview above the list: alert for fleets under attack, counters, damage, upkeep and the
 * next arrivals. Re-renders every second only for the countdowns (useNow); the summary itself is
 * recomputed when the fleet list changes.
 */
export function FleetOverview({ fleets, systemName, onSelect }: Props) {
  const now = useNow();
  const base = useMemo(() => summarizeFleets(fleets, Date.now()), [fleets]);
  const next = base.arrivals.filter(a => a.at > now - 1000).slice(0, 3);
  if (base.total === 0) return null;

  return (
    <section className="space-y-2" aria-label="Flotta áttekintés" data-testid="fleet-overview">
      {base.inCombat.length > 0 && (
        <div role="alert" data-testid="fleet-alert" className="rounded-xl border border-red-500/60 bg-red-950/40 p-3 space-y-2">
          <p className="text-sm font-semibold text-red-300">⚠ {base.inCombat.length} flotta harcban áll</p>
          <div className="flex flex-wrap gap-2">
            {base.inCombat.map(f => (
              <button key={f.id} type="button" onClick={() => onSelect(f.id)} data-testid="fleet-alert-fleet"
                className="rounded-lg border border-red-500/50 text-red-200 text-xs px-3 truncate max-w-full" style={{ minHeight: 44 }}>
                {f.name} · {systemName(f.currentSystemId)}
              </button>
            ))}
          </div>
        </div>
      )}

      <dl className="grid grid-cols-4 gap-2 text-center" data-testid="fleet-overview-stats">
        <Counter label="Úton" value={base.traveling} color="#00e5ff" testId="ov-traveling" />
        <Counter label="Dolgozik" value={base.working} color="#ffa726" testId="ov-working" />
        <Counter label="Vár" value={base.idle} color="#94a3b8" testId="ov-idle" />
        <Counter label="Sérült" value={base.damagedShips} color={base.damagedShips > 0 ? '#fbbf24' : '#64748b'} testId="ov-damaged" hint="hajó" />
      </dl>

      <div className="flex items-center justify-between text-xs text-slate-400 px-1" data-testid="fleet-upkeep">
        <span>Flotta-fenntartás</span>
        <span className="font-mono text-slate-200">{base.upkeepMetalPerHour} fém / óra</span>
      </div>

      {next.length > 0 && (
        <div className="sci-fi-panel rounded-xl p-3 space-y-1" data-testid="fleet-arrivals">
          <h2 className="text-[11px] uppercase tracking-wide text-slate-500">Következő érkezések</h2>
          <ul className="space-y-1">
            {next.map(a => (
              <li key={a.fleetId}>
                <button type="button" onClick={() => onSelect(a.fleetId)} className="w-full flex items-center justify-between gap-2 text-left text-xs text-slate-300" style={{ minHeight: 44 }}>
                  <span className="truncate">{a.name} → <span className="text-cyan-300">{systemName(a.systemId)}</span></span>
                  <span className="font-mono text-cyan-400/90 flex-shrink-0" title={formatEta(a.at, now)}>{formatDuration(Math.max(0, a.at - now))}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Counter({ label, value, color, testId, hint }: { label: string; value: number; color: string; testId: string; hint?: string }) {
  return (
    <div className="bg-slate-900/60 rounded-lg py-2">
      <dt className="text-[10px] uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-lg font-mono" style={{ color }} data-testid={testId}>{value}{hint && <span className="text-[10px] text-slate-500"> {hint}</span>}</dd>
    </div>
  );
}
